import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Bell, BellOff, Check, CheckCheck, Settings, Radio, UserPlus,
  DollarSign, Calendar, MessageCircle, Megaphone, Trash2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationCenter = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'settings'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      await axios.put(`${API}/notifications/settings`, null, {
        params: { [key]: value }
      });
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'stream_live':
      case 'streamer_went_live':
        return <Radio className="w-5 h-5 text-red-500" />;
      case 'new_follower':
        return <UserPlus className="w-5 h-5 text-blue-500" />;
      case 'tip':
      case 'tips_received':
        return <DollarSign className="w-5 h-5 text-green-500" />;
      case 'scheduled_reminder':
      case 'stream_scheduled':
        return <Calendar className="w-5 h-5 text-amber-500" />;
      case 'new_message':
        return <MessageCircle className="w-5 h-5 text-purple-500" />;
      case 'costream_invite':
        return <Radio className="w-5 h-5 text-pink-500" />;
      case 'verification_approved':
        return <Check className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="notification-center"
      >
        {/* Header */}
        <div className="p-6 border-b" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#111111' }}>Notifications</h2>
                {unreadCount > 0 && (
                  <p className="text-sm text-purple-500">{unreadCount} unread</p>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5">
              <X className="w-5 h-5" style={{ color: '#6B7280' }} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'all' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'hover:bg-black/5 text-gray-600'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'settings' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'hover:bg-black/5 text-gray-600'
              }`}
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            {activeTab === 'all' && unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="ml-auto px-3 py-2 rounded-xl text-sm font-medium text-purple-600 hover:bg-purple-50 flex items-center gap-1"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'all' ? (
            loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-3 border-purple-500 border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <BellOff className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notif.read ? 'bg-purple-50/50' : ''
                    }`}
                    onClick={() => !notif.read && handleMarkAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.read ? 'font-medium' : ''}`} style={{ color: '#111111' }}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{notif.body}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-500 mb-4">Choose what notifications you want to receive</p>
              
              {settings && Object.entries({
                stream_live: { label: 'Followed streamers go live', icon: Radio },
                new_follower: { label: 'New followers', icon: UserPlus },
                tips_received: { label: 'Tips received', icon: DollarSign },
                scheduled_reminder: { label: 'Scheduled stream reminders', icon: Calendar },
                new_message: { label: 'New messages', icon: MessageCircle },
                marketing: { label: 'Marketing & promotions', icon: Megaphone }
              }).map(([key, { label, icon: Icon }]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-400" />
                    <span className="text-sm" style={{ color: '#111111' }}>{label}</span>
                  </div>
                  <button
                    onClick={() => handleUpdateSettings(key, !settings[key])}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      settings[key] ? 'bg-purple-500' : 'bg-gray-200'
                    }`}
                  >
                    <div 
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        settings[key] ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationCenter;
